import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import * as css from './WhatsAppScreen.css.js';
import {updateWhatsAppMessage, saveFreeWhatsAppMessage, updateEmailStatus, saveWhatsAppImage, updateDesignEmail} from '../../../redux/branding/brandingActions';
import ModeBtn from '../../../component/modeBtn/ModeBtn';
import WhatsAppTemplateEditor from '../../../component/whatsAppTemplateEditor/WhatsAppTemplateEditor';
import WhatsAppEditorWrapper from './WhatsAppEditorWrapper.js';
import {useSelector} from 'react-redux';
import WhatsAppImage from './WhatsAppImage.js';
import {IS_NEW_TEMPLATE} from '../../../config/config';
import {updateEventSettings} from '../../../redux/event/eventActions';
import {setCurrentRating} from '../../components/rating/Rating';
import {TState} from '../../../redux/store.type';
import {TBranding} from '../../../redux/branding/branding.type';
import {useBrandingParams} from '../../../hooks/useBrandingParams';
import TrialPop from '../../../component/sendingPopup/components/trialPop/TrialPop';
import {useTranslation} from 'react-i18next';
import useGlobalShow from '../../../hooks/useGlobalShow.js';
import {serializeTemplateStructure} from '../../../component/whatsAppTemplateEditor/utils/formatUtils';
import Loading from '../../mailing/Shared/Loading/Loading.js';
import {useUnsavedChangesWarning} from '../../../hooks/useUnsavedChangesWarning';
import {BrandingContext} from '../context/BrandingContext';
import {TextTemplateItem, TemplateStructure} from '../../../component/whatsAppTemplateEditor/types/templateStructure.js';
import AIWhatsappGenerator from '../../../component/aiWhatsappGenerator/AIWhatsappGenerator';
import {getUserPreferences} from '../../../redux/user/userActions';
import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import useRestartXHR from '../../../hooks/useRestartXHR';
import {dismissLoaderPopupAI} from '../../../component/loaderPopupAI/dismissLoaderPopupAI';
import {setWhatsAppEditorMode} from '../../../component/whatsAppTemplateEditor/hooks/useWhatsAppEditorMode';

function WhatsAppScreen() {
    const {eid, defaultLang, uid} = useSelector((s: TState) => ({
        eid: s.event.eid,
        defaultLang: s.event.lang,
        uid: s.user?.uid ?? null,
    }));
    const {messageType}=useBrandingParams();
    const {currentLang,data,getBrandingData} = useSelector((s: TState) => s.branding??{} as TBranding);
    const {whatsappContent,enabledWhatsapp}=data?.[currentLang]?.[messageType]??{};
    const dependencies=[currentLang,getBrandingData[`${currentLang}XHR`], messageType, whatsappContent];
    const image = data?.[currentLang]?.[messageType]?.whatsappImage || data?.[defaultLang]?.whatsappImage;
    const newService = IS_NEW_TEMPLATE(Number(eid));
    const show = useGlobalShow('isOpenTrialPop');
    const trialPopupShow = useGlobalShow('isOpenTrialPopup');
    const saveWhatsAppImageXHR = useSelector((s: TState) => s.branding.saveWhatsAppImageFullXHR);
    const getUserPreferencesXHR = useSelector((s: TState) => s.user.getUserPreferencesXHR);
    const saveFreeWhatsAppMessageXHR = useSelector((s: TState) => s.branding.saveFreeWhatsAppMessageXHR);
    const updateWhatsAppMessageXHR = useSelector((s: TState) => s.branding.updateWhatsAppMessageXHR);
    useRestartXHR(saveFreeWhatsAppMessageXHR, 'branding.saveFreeWhatsAppMessageXHR');
    useRestartXHR(updateWhatsAppMessageXHR, 'branding.updateWhatsAppMessageXHR');
    const {whatsapp: {message, setMessage, aiGeneratorShow}} = useContext(BrandingContext);
    const initialMessageContent = useMemo(() => 
        newService ? serializeTemplateStructure(whatsappContent ?? []) : whatsappContent?.replace(/\\n/g, '\n') || '',
    [newService, whatsappContent]
    );
    const savedBaselineRef = useRef(initialMessageContent);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const loaderSuppressed = useSelector((s: TState) => Boolean(s.user?.preferences?.hideWhatsappFreeLoader));
    useEffect(() => {
        setMessage(initialMessageContent);
        savedBaselineRef.current = initialMessageContent;
        setHasUnsavedChanges(false);
    }, [initialMessageContent, setMessage]);

    useEffect(() => {
        if(uid && !getUserPreferencesXHR) {
            void getUserPreferences();
        }
    }, [uid, getUserPreferencesXHR]);
    const {t} = useTranslation('whatsapp');
    
    // Add unsaved changes warning
    useUnsavedChangesWarning({hasUnsavedChanges});

    const updateStatus = async () => {
        await updateEmailStatus('enabledWhatsapp', (enabledWhatsapp || 0) === 1 ? 0 : 1, messageType);
        if(!enabledWhatsapp) {
            await updateEventSettings('whatsAppMessage', {active: (enabledWhatsapp || 0) === 1 ? false : true, messageType, lang: currentLang}, 'ticketsMailing');
        }
    };
    type WhatsAppContent = string | TextTemplateItem[] | TemplateStructure;

    const saveText = useCallback(async (content: WhatsAppContent) => {
        let savedValue = typeof content === 'string' ? content : (message ?? '');

        if(newService) {
            const currentTemplate = Array.isArray(whatsappContent) ? whatsappContent : [];
            const freeMessageValue = typeof content === 'string' ? content : message ?? '';
            const templateSnapshot = typeof content === 'string' ? currentTemplate : content;
            const currentWhatsappEditorMode = store.getState().ui.whatsappEditorMode?.[messageType] ?? 'template';
            const shouldSaveFree = currentWhatsappEditorMode === 'free';

            if(shouldSaveFree) {
                const shouldShowLoader = getUserPreferencesXHR === 'SUCCESS' && !loaderSuppressed;
                if(shouldShowLoader) {
                    setGlobalState('ui.show.isOpenAiLoaderPopup', true);
                }
                const savedContent = await saveFreeWhatsAppMessage(freeMessageValue, messageType);
                if(shouldShowLoader) {
                    dismissLoaderPopupAI();
                }
                if(savedContent) {
                    savedValue = serializeTemplateStructure(savedContent);
                    setMessage(savedValue);
                    setWhatsAppEditorMode(messageType, 'template');
                }
            } else {
                updateWhatsAppMessage(templateSnapshot, messageType, null);
            }
        } else {
            updateDesignEmail(messageType, {whatsappContent: content, content, mailingType: 'whatsapp'});
        }

        savedBaselineRef.current = savedValue;
        setHasUnsavedChanges(false);
    }, [newService, messageType, whatsappContent, message, loaderSuppressed, getUserPreferencesXHR, setMessage]);

    const handleStructureChange = useCallback((structure: TemplateStructure) => {
        const serialized = serializeTemplateStructure(structure);
        setMessage(serialized);
        setHasUnsavedChanges(serialized !== savedBaselineRef.current);
    }, [setMessage]);

    const handleFreeMessageChange = useCallback((content: string) => {
        setMessage(content);
        setHasUnsavedChanges(content !== savedBaselineRef.current);
    }, [setMessage]);

    useEffect(() => {
        if(newService){
            setCurrentRating('whatsappNewService');
        }
    }, []);
    
    const isLoading = getBrandingData?.[`${currentLang}XHR`] !== 'SUCCESS';
    
    return (
        <css.wrapper data-cy={'whatsAppScreen'}>
            <css.top data-cy="whatsapp-screen-top">
                {t('whatsappMailing')}
                <ModeBtn className={'small'} status={enabledWhatsapp} click={updateStatus} requestStatus={undefined} dataTitle={undefined} text={undefined} dataCyName={'whatsapp-mailing-status'}/>
            </css.top>
            <WhatsAppImage name="whatsappImage" eyeName="whatsappImage" image={image} saveImage={(image)=>saveWhatsAppImage(image, messageType)} imageXHR={saveWhatsAppImageXHR}/>
            {isLoading || !whatsappContent ? (
                <Loading />
            ) : newService ? (
                <WhatsAppTemplateEditor
                    data-cy={'whatsAppTemplateEditor'}
                    modeKey={messageType}
                    templateStructure={whatsappContent}
                    dependencies={dependencies}
                    variablesType="whatsapp"
                    freeMessage={message ?? ''}
                    onFreeMessageChange={handleFreeMessageChange}
                    saveXHR={saveFreeWhatsAppMessageXHR || updateWhatsAppMessageXHR}
                    events={{onSave: saveText, onChange: handleStructureChange}}
                />
            ) : (
                <WhatsAppEditorWrapper
                    defaultText={message}
                    dependencies={dependencies}
                    events={{onSave: saveText, onChange: handleStructureChange}}
                    variablesType="whatsapp"
                    limit={1000}
                />
            )}
            {newService && (
                <AIWhatsappGenerator
                    show={aiGeneratorShow}
                    messageType={messageType}
                    updateMessage={handleStructureChange}
                />
            )}
            {show.isOpen && (
                <TrialPop
                    show={show}
                    messageContent={initialMessageContent}
                    contactType="phone"
                    messageText={initialMessageContent}
                    imagePath={image?.path || ''}
                    messageType={messageType}
                    currentLang={currentLang}
                    trialPopupShow={trialPopupShow}
                />
            )}
        </css.wrapper>
    );
}

export default WhatsAppScreen;
